import type { FC, ReactNode, SVGProps } from "react"
import { useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link, useParams } from "@tanstack/react-router"
import {
	IconBellOutline18 as Bell,
	IconCheckOutline18 as Check,
	IconDotsOutline18 as Dots,
	IconHashtagOutline18 as Hash,
	IconInboxOutline18 as InboxIcon,
	IconLayersStackedOutline18 as LayersStacked,
	IconLockOutline18 as Lock,
	IconStackPerspectiveOutline18 as StackPerspective,
	IconRadarOutline18 as Radar,
	IconArrowRotateClockwiseOutline18 as Refresh,
	IconMagnifierOutline18 as Search,
	IconTagOutline18 as TagIcon,
	IconUserOutline18 as User,
	IconXmarkOutline18 as Xmark,
} from "nucleo-ui-outline-18"

import type { UpdateSlackChannel } from "@productlane/zero/types"
import { cn } from "@productlane/lib/src/cn"
import {
	AlertDialog,
	Avatar,
	Badge,
	Button,
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuItem,
	Empty,
	IconButton,
	Input,
	Loader,
	SortableTableHeader,
} from "@productlane/ui"
import {
	DropdownMenuPortal,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
} from "@productlane/ui/src/components/internal/dropdown"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { ChannelSort } from "./slack-channel-list"
import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { useSlackConnections, useZero } from "@/lib/zero"
import { SettingsHeader } from "../settings-header"
import {
	bulkEditableIds,
	CHANNEL_GROUPING_OPTIONS,
	CHANNEL_PAGE_SIZE,
	channelGroupingLabel,
	channelGroupingValue,
	DEFAULT_CHANNEL_SORT,
	filterChannels,
	parseChannelGrouping,
	slackDomain,
	sortChannels,
} from "./slack-channel-list"
import {
	CHANNEL_GRID,
	FILL_TO_BOTTOM,
	GROUP_BAND,
	INSET,
	INSET_PADDING,
	META_CELL,
	PAGE_BLEED,
	PAGE_GUTTER,
	PINNED_NOTE,
	ROW_HOVER,
} from "./slack-channels-table"

type ChannelRowData = NonNullable<
	ReturnType<typeof useChannels>["channels"]
>[number]
type ChannelPatch = Omit<UpdateSlackChannel, "id">
type Icon = FC<SVGProps<SVGSVGElement>>

interface Choice {
	id: string
	label: string
}

function useChannels(slackConnectSettingsId: string) {
	const [channels, result] = useQuery(
		queries.slackChannels({ slackConnectSettingsId }),
	)
	return { channels, isLoaded: result.type === "complete" }
}

// Off keeps the same box as on, so toggling never shifts the columns down the list.
function UsagePills({
	inbox,
	changelog,
}: {
	inbox: boolean
	changelog: boolean
}) {
	return (
		<>
			<Badge
				variant={inbox ? "default" : "dashed"}
				Icon={InboxIcon}
				className="transform-gpu"
			>
				Inbox
			</Badge>
			<Badge
				variant={changelog ? "default" : "dashed"}
				Icon={StackPerspective}
				className="transform-gpu"
			>
				Changelog
			</Badge>
		</>
	)
}

function UsageMenu({
	channel,
	onChange,
}: {
	channel: ChannelRowData
	onChange: (patch: ChannelPatch) => void
}) {
	return (
		<DropdownMenu
			trigger={
				<button
					type="button"
					className="flex items-center gap-1 rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
				>
					<UsagePills inbox={channel.inbox} changelog={channel.changelog} />
				</button>
			}
			contentProps={{ align: "end" }}
		>
			<DropdownMenuCheckboxItem
				checked={channel.inbox}
				onCheckedChange={(inbox) => onChange({ inbox })}
				onSelect={(e) => e.preventDefault()}
			>
				<span className="flex items-center gap-2">
					<InboxIcon className="size-4 flex-none text-secondary" />
					Inbox
				</span>
			</DropdownMenuCheckboxItem>
			<DropdownMenuCheckboxItem
				checked={channel.changelog}
				onCheckedChange={(changelog) => onChange({ changelog })}
				onSelect={(e) => e.preventDefault()}
			>
				<span className="flex items-center gap-2">
					<StackPerspective className="size-4 flex-none text-secondary" />
					Changelog
				</span>
			</DropdownMenuCheckboxItem>
		</DropdownMenu>
	)
}

function ChoiceSubmenu({
	Icon,
	label,
	choices,
	selected,
	onSelect,
}: {
	Icon: Icon
	label: string
	choices: Choice[]
	selected: string
	onSelect: (id: string) => void
}) {
	return (
		<DropdownMenuSub>
			<DropdownMenuSubTrigger className="gap-2">
				<Icon className="size-4 flex-none text-secondary" />
				{label}
			</DropdownMenuSubTrigger>
			<DropdownMenuPortal>
				<DropdownMenuSubContent className="max-h-80 overflow-y-auto pb-2">
					{choices.map((choice) => (
						<DropdownMenuItem
							key={choice.id}
							Icon={choice.id === selected ? Check : undefined}
							onSelect={() => onSelect(choice.id)}
						>
							{choice.label}
						</DropdownMenuItem>
					))}
				</DropdownMenuSubContent>
			</DropdownMenuPortal>
		</DropdownMenuSub>
	)
}

const NONE = "__none__"

function ChannelRow({
	channel,
	isNotificationChannel,
	canEdit,
	tags,
	members,
	onChange,
}: {
	channel: ChannelRowData
	isNotificationChannel: boolean
	canEdit: boolean
	tags: Choice[]
	members: Choice[]
	onChange: (patch: ChannelPatch) => void
}) {
	const grouping = channelGroupingLabel(channel)
	const tagColor = channel.tag?.tagGroup?.color ?? channel.tag?.color
	const assigneeName = channel.assignee
		? (channel.assignee.name ?? channel.assignee.email)
		: null
	return (
		<div className={cn(CHANNEL_GRID, ROW_HOVER, "group h-10 rounded-md")}>
			<div className="flex min-w-0 items-center gap-2">
				{channel.isPrivate ? (
					<Lock className="size-3.5 flex-none text-tertiary" />
				) : (
					<Hash className="size-3.5 flex-none text-tertiary" />
				)}
				<span className="truncate text-primary">{channel.name}</span>
				{channel.isShared && (
					<span className="flex-none text-xs text-tertiary">Shared</span>
				)}
			</div>
			<div className={cn(META_CELL, "text-secondary")}>
				<span className="truncate">{grouping}</span>
			</div>
			<div className={cn(META_CELL, "text-secondary")}>
				{channel.tag && (
					<>
						<span
							className="size-1.5 flex-none rounded-full"
							style={{ backgroundColor: tagColor }}
						/>
						<span className="truncate">{channel.tag.name}</span>
					</>
				)}
			</div>
			<div className={cn(META_CELL, "text-secondary")}>
				{channel.assignee && assigneeName && (
					<>
						<Avatar
							name={assigneeName}
							image={channel.assignee.imageUrl ?? undefined}
							size="xs"
							className="flex-none"
						/>
						<span className="truncate">{assigneeName}</span>
					</>
				)}
			</div>
			<div className="flex justify-end">
				{isNotificationChannel ? (
					<div className="flex items-center gap-1 p-1">
						<Badge variant="transparent" Icon={Bell} className="text-secondary">
							Notifications
						</Badge>
					</div>
				) : canEdit ? (
					<UsageMenu channel={channel} onChange={onChange} />
				) : (
					<div className="flex items-center gap-1 p-1">
						<UsagePills inbox={channel.inbox} changelog={channel.changelog} />
					</div>
				)}
			</div>
			{canEdit ? (
				<DropdownMenu
					trigger={
						<IconButton
							Icon={Dots}
							variant="ghost"
							aria-label={`Options for ${channel.name}`}
							className="opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
						/>
					}
					contentProps={{ align: "end" }}
				>
					<ChoiceSubmenu
						Icon={LayersStacked}
						label="Message grouping"
						choices={CHANNEL_GROUPING_OPTIONS.map((option) => ({
							id: option.value,
							label: option.label,
						}))}
						selected={channelGroupingValue(channel)}
						onSelect={(value) => onChange(parseChannelGrouping(value))}
					/>
					<ChoiceSubmenu
						Icon={TagIcon}
						label="Default tag"
						choices={[{ id: NONE, label: "No tag" }, ...tags]}
						selected={channel.tagId ?? NONE}
						onSelect={(id) => onChange({ tagId: id === NONE ? null : id })}
					/>
					<ChoiceSubmenu
						Icon={User}
						label="Default assignee"
						choices={[{ id: NONE, label: "No assignee" }, ...members]}
						selected={channel.assigneeId ?? NONE}
						onSelect={(id) => onChange({ assigneeId: id === NONE ? null : id })}
					/>
				</DropdownMenu>
			) : (
				<span />
			)}
		</div>
	)
}

function SetAllMenu({
	count,
	onSet,
}: {
	count: number
	onSet: (patch: Pick<ChannelPatch, "inbox" | "changelog">) => void
}) {
	return (
		<DropdownMenu
			trigger={
				<Button variant="ghost" size="sm">
					Set all {count}
				</Button>
			}
			contentProps={{ align: "end" }}
		>
			<DropdownMenuItem
				Icon={InboxIcon}
				onSelect={() => onSet({ inbox: true })}
			>
				Turn inbox on
			</DropdownMenuItem>
			<DropdownMenuItem
				Icon={InboxIcon}
				onSelect={() => onSet({ inbox: false })}
			>
				Turn inbox off
			</DropdownMenuItem>
			<DropdownMenuItem
				Icon={StackPerspective}
				onSelect={() => onSet({ changelog: true })}
			>
				Turn changelog on
			</DropdownMenuItem>
			<DropdownMenuItem
				Icon={StackPerspective}
				onSelect={() => onSet({ changelog: false })}
			>
				Turn changelog off
			</DropdownMenuItem>
		</DropdownMenu>
	)
}

function ChannelGroup({
	title,
	channels,
	notificationChannelId,
	canEdit,
	tags,
	members,
	onChange,
}: {
	title: string
	channels: ChannelRowData[]
	notificationChannelId: string | null | undefined
	canEdit: boolean
	tags: Choice[]
	members: Choice[]
	onChange: (ids: string[], patch: ChannelPatch) => void
}) {
	const [limit, setLimit] = useState(CHANNEL_PAGE_SIZE)
	if (channels.length === 0) return null
	const hidden = channels.length - limit
	return (
		<div>
			<div className={GROUP_BAND}>
				{title}
				<span className="text-tertiary">{channels.length}</span>
				<div className="flex-1" />
				{canEdit && (
					<SetAllMenu
						count={channels.length}
						onSet={(patch) =>
							onChange(bulkEditableIds(channels, notificationChannelId), patch)
						}
					/>
				)}
			</div>
			{channels.slice(0, limit).map((channel) => (
				<ChannelRow
					key={channel.id}
					channel={channel}
					isNotificationChannel={channel.channelId === notificationChannelId}
					canEdit={canEdit}
					tags={tags}
					members={members}
					onChange={(patch) => onChange([channel.id], patch)}
				/>
			))}
			{hidden > 0 && (
				<ShowMoreRow
					onClick={() => setLimit((current) => current + CHANNEL_PAGE_SIZE)}
				>
					Show {Math.min(hidden, CHANNEL_PAGE_SIZE)} more
				</ShowMoreRow>
			)}
		</div>
	)
}

function ShowMoreRow({
	onClick,
	children,
}: {
	onClick: () => void
	children: ReactNode
}) {
	return (
		<div className={INSET}>
			<button
				type="button"
				className={cn(
					"flex h-9 w-full items-center rounded-md text-xs font-medium text-secondary hover:text-primary",
					INSET_PADDING,
					ROW_HOVER,
				)}
				onClick={onClick}
			>
				{children}
			</button>
		</div>
	)
}

interface NotJoinedRow {
	id: string
	name: string
}

function NotJoinedGroup({
	channels,
	total,
	canEdit,
	hasMore,
	joiningId,
	onJoin,
	onJoinAll,
	onShowMore,
}: {
	channels: readonly NotJoinedRow[]
	/** Every not-joined channel, not just the loaded page; null while searching. */
	total: number | null
	canEdit: boolean
	hasMore: boolean
	joiningId: string | null
	onJoin: (id: string) => void
	onJoinAll: () => void
	onShowMore: () => void
}) {
	if (channels.length === 0) return null
	return (
		<div>
			<div className={GROUP_BAND}>
				Not joined
				<span className="text-tertiary">{total ?? channels.length}</span>
				<div className="flex-1" />
				{canEdit && total !== null && (
					<Button variant="ghost" size="sm" onClick={onJoinAll}>
						Join all {total}
					</Button>
				)}
			</div>
			{channels.map((channel) => (
				<div
					key={channel.id}
					className={cn(CHANNEL_GRID, ROW_HOVER, "h-10 rounded-md")}
				>
					<div className="flex min-w-0 items-center gap-2">
						<Hash className="size-3.5 flex-none text-tertiary" />
						<span className="truncate text-secondary">{channel.name}</span>
					</div>
					<span className={META_CELL} />
					<span className={META_CELL} />
					<span className={META_CELL} />
					<div className="flex justify-end">
						{canEdit && (
							<Button
								variant="outline"
								size="sm"
								isLoading={joiningId === channel.id}
								disabled={joiningId !== null}
								onClick={() => onJoin(channel.id)}
							>
								Join
							</Button>
						)}
					</div>
					<span />
				</div>
			))}
			{hasMore && <ShowMoreRow onClick={onShowMore}>Show more</ShowMoreRow>}
		</div>
	)
}

export function SlackChannelsPage() {
	const { connectionId = "" } = useParams({ strict: false })
	const zero = useZero()
	const utils = api.useUtils()
	const { role } = useRole()
	const canEdit = role !== "VIEWER"
	const [query, setQuery] = useState("")
	const [sort, setSort] = useState<ChannelSort>(DEFAULT_CHANNEL_SORT)

	const settings = useSlackConnections().find((row) => row.id === connectionId)
	const slackQuery = api.workspace.slackSettings.useQuery(undefined, {
		staleTime: 30_000,
	})
	const status = slackQuery.data?.connections.find(
		(row) => row.id === connectionId,
	)
	const { channels, isLoaded } = useChannels(connectionId)
	const [notJoinedLimit, setNotJoinedLimit] = useState(CHANNEL_PAGE_SIZE)
	const [notJoined] = useQuery(
		queries.slackChannelsNotJoined({
			slackConnectSettingsId: connectionId,
			search: query.slice(0, 100),
			limit: notJoinedLimit,
			// Not-joined rows only have a name, so only the name sort reaches them.
			order: sort === "name-desc" ? "desc" : "asc",
		}),
	)
	const [joiningId, setJoiningId] = useState<string | null>(null)
	const [joinAllOpen, setJoinAllOpen] = useState(false)
	const [tagRows] = useQuery(queries.searchTags({ query: "", limit: 200 }))
	const [memberRows] = useQuery(queries.teamMembers())

	const sync = api.workspace.syncSlackChannels.useMutation({
		onSuccess: () => {
			toast.success({ title: "Channels refreshed from Slack" })
			void utils.workspace.slackSettings.invalidate()
		},
		onError: (error) =>
			toast.error({
				title: "Channels didn't refresh",
				description: error.message,
			}),
	})
	const join = api.workspace.joinSlackChannels.useMutation({
		onSuccess: (result) => {
			void utils.workspace.slackSettings.invalidate()
			if (result.queued) {
				toast.success({
					title: `Joining ${result.count} channels`,
					description: "They move up as the bot joins them.",
				})
			} else if (result.failed > 0) {
				toast.error({ title: "Slack didn't let the bot join that channel" })
			}
		},
		onError: (error) =>
			toast.error({ title: "The bot didn't join", description: error.message }),
		onSettled: () => setJoiningId(null),
	})

	// The Slack page's channel counts read the same rows, so refresh them on the way back.
	useEffect(
		() => () => void utils.workspace.slackSettings.invalidate(),
		[utils],
	)

	const tags = useMemo(
		() =>
			[...tagRows]
				.sort((a, b) => a.name.localeCompare(b.name))
				.map((tag) => ({ id: tag.id, label: tag.name })),
		[tagRows],
	)
	const members = useMemo(
		() =>
			memberRows
				.filter((member) => member.role !== "VIEWER" && member.user)
				.map((member) => ({
					id: member.userId,
					label: member.user?.name || member.user?.email || "Unknown",
				}))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[memberRows],
	)

	const groups = useMemo(() => {
		const { inbox, other } = filterChannels(channels ?? [], query)
		return {
			inbox: sortChannels(inbox, sort),
			other: sortChannels(other, sort),
		}
	}, [channels, query, sort])

	if (!settings) {
		return (
			<>
				<SettingsHeader title="Slack workspace not found" />
				<Link
					to="/settings/integrations/slack"
					className="text-sm font-medium text-secondary hover:text-primary"
				>
					Back to Slack
				</Link>
			</>
		)
	}

	const update = async (ids: string[], patch: ChannelPatch) => {
		if (ids.length === 0) return
		const write =
			ids.length === 1 && ids[0]
				? mutators.slackChannels.update({ id: ids[0], ...patch })
				: mutators.slackChannels.updateMany({
						ids,
						inbox: patch.inbox,
						changelog: patch.changelog,
					})
		const result = await zero.mutate(write).client
		if (result.type === "error") {
			toast.error({
				title: "Failed to update",
				description: "Please try again",
			})
		}
	}

	const domain = slackDomain(settings.teamUrl)
	const name = status?.slackTeam?.name ?? domain
	const groupProps = {
		notificationChannelId: settings.notificationChannelId,
		canEdit,
		tags,
		members,
		onChange: (ids: string[], patch: ChannelPatch) => void update(ids, patch),
	}
	const search = query.trim()
	const isLoading = !isLoaded && !channels?.length
	const isEmpty =
		!isLoading &&
		groups.inbox.length + groups.other.length + notJoined.length === 0
	const refresh = () => sync.mutate({ slackConnectSettingsId: settings.id })

	return (
		<>
			<MetaInfo title={`${name} channels`} />
			<SettingsHeader
				title="Channels"
				subtitle={`${name} workspace, ${domain}`}
			/>
			<div
				className={cn(PAGE_BLEED, FILL_TO_BOTTOM, "flex grow flex-col text-sm")}
			>
				<div className={cn("flex items-center gap-2 pb-4", PAGE_GUTTER)}>
					<Input
						name="search"
						icon={Search}
						placeholder="Search channels"
						className="w-64"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
					/>
					<div className="flex-1" />
					<IconButton
						Icon={Refresh}
						variant="ghost"
						title="Refresh from Slack"
						aria-label="Refresh from Slack"
						disabled={sync.isLoading}
						iconClassName={cn(
							"transform-gpu",
							sync.isLoading && "animate-spin",
						)}
						onClick={refresh}
					/>
				</div>
				{isEmpty ? (
					<div className="flex grow items-center justify-center py-12">
						<Empty
							quiet
							title={
								search
									? `No channels match "${search}"`
									: "No channels yet. Add the bot to a channel in Slack, then refresh."
							}
							image={
								<Radar strokeWidth={0.5} className="h-16 w-16 text-secondary" />
							}
							action={
								search ? (
									<Button
										variant="outline"
										size="sm"
										iconRight={Xmark}
										onClick={() => setQuery("")}
									>
										Clear filters
									</Button>
								) : (
									<Button
										variant="outline"
										size="sm"
										Icon={Refresh}
										isLoading={sync.isLoading}
										onClick={refresh}
									>
										Refresh from Slack
									</Button>
								)
							}
						/>
					</div>
				) : (
					<>
						<div className={cn(CHANNEL_GRID, "h-8 text-xs text-secondary")}>
							<SortableTableHeader
								label="Name"
								currentSortType={sort}
								sortTypeAsc="name-asc"
								sortTypeDesc="name-desc"
								onClick={setSort}
							/>
							<span className={META_CELL}>
								<SortableTableHeader
									label="Message grouping"
									currentSortType={sort}
									sortTypeAsc="grouping-asc"
									sortTypeDesc="grouping-desc"
									onClick={setSort}
								/>
							</span>
							<span className={META_CELL}>
								<SortableTableHeader
									label="Default tag"
									currentSortType={sort}
									sortTypeAsc="tag-asc"
									sortTypeDesc="tag-desc"
									onClick={setSort}
								/>
							</span>
							<span className={META_CELL}>
								<SortableTableHeader
									label="Default assignee"
									currentSortType={sort}
									sortTypeAsc="assignee-asc"
									sortTypeDesc="assignee-desc"
									onClick={setSort}
								/>
							</span>
							<span />
							<span />
						</div>
						{isLoading ? (
							<div className="flex justify-center py-8">
								<Loader />
							</div>
						) : (
							<>
								<ChannelGroup
									title="Inbox"
									channels={groups.inbox}
									{...groupProps}
								/>
								<ChannelGroup
									title="Not an inbox"
									channels={groups.other}
									{...groupProps}
								/>
								<NotJoinedGroup
									channels={notJoined}
									total={search ? null : (status?.notJoinedChannels ?? null)}
									canEdit={canEdit}
									hasMore={notJoined.length === notJoinedLimit}
									joiningId={joiningId}
									onJoin={(id) => {
										setJoiningId(id)
										join.mutate({
											slackConnectSettingsId: settings.id,
											slackChannelIds: [id],
										})
									}}
									onJoinAll={() => setJoinAllOpen(true)}
									onShowMore={() =>
										setNotJoinedLimit((current) => current + CHANNEL_PAGE_SIZE)
									}
								/>
							</>
						)}
					</>
				)}
				<p className={PINNED_NOTE}>
					Private and shared channels show up after someone types /invite
					@Productlane in them.
				</p>
			</div>
			<AlertDialog
				variant="positive"
				isOpen={joinAllOpen}
				onOpenChange={setJoinAllOpen}
				confirmText="Join channels"
				title={`Join ${status?.notJoinedChannels ?? 0} channels?`}
				description="Slack shows a join message in each channel."
				onCancel={() => setJoinAllOpen(false)}
				onConfirm={() => {
					setJoinAllOpen(false)
					join.mutate({ slackConnectSettingsId: settings.id })
				}}
			/>
		</>
	)
}
